import React, { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import './Form.css';

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const TextField = React.forwardRef<HTMLInputElement, FieldProps>(
  ({ label, error, id, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const errorId = `${inputId}-error`;

    return (
      <div className="auth-field">
        {label && (
          <label className="auth-field__label" htmlFor={inputId}>
            {label}
          </label>
        )}
        <div className="auth-field__control">
          <input
            ref={ref}
            id={inputId}
            className={`auth-field__input${error ? ' auth-field__input--error' : ''}`}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            {...props}
          />
        </div>
        {error && (
          <p className="auth-field__error" id={errorId}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';

interface PasswordFieldProps extends FieldProps {
  showLabel?: string;
  hideLabel?: string;
}

export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  (
    {
      label,
      error,
      id,
      showLabel = 'Afficher le mot de passe',
      hideLabel = 'Masquer le mot de passe',
      ...props
    },
    ref
  ) => {
    const [visible, setVisible] = useState(false);
    const autoId = useId();
    const inputId = id ?? autoId;
    const errorId = `${inputId}-error`;

    return (
      <div className="auth-field">
        {label && (
          <label className="auth-field__label" htmlFor={inputId}>
            {label}
          </label>
        )}
        <div className="auth-field__control">
          <input
            ref={ref}
            id={inputId}
            type={visible ? 'text' : 'password'}
            className={`auth-field__input auth-field__input--password${error ? ' auth-field__input--error' : ''}`}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            {...props}
          />
          <button
            type="button"
            className="auth-field__toggle"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? hideLabel : showLabel}
            tabIndex={-1}
          >
            {visible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {error && (
          <p className="auth-field__error" id={errorId}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

PasswordField.displayName = 'PasswordField';

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, ...props }, ref) => (
    <label className="auth-checkbox">
      <input ref={ref} type="checkbox" className="auth-checkbox__input" {...props} />
      {label}
    </label>
  )
);

Checkbox.displayName = 'Checkbox';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  withArrow?: boolean;
  variant?: 'primary' | 'ghost';
}

export function Button({
  isLoading = false,
  withArrow = false,
  variant = 'primary',
  disabled,
  children,
  className = '',
  ...props
}: ButtonProps) {
  return (
    <button
      className={`auth-button${variant === 'ghost' ? ' auth-button--ghost' : ''} ${className}`.trim()}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <span className="auth-spinner" aria-hidden="true" />}
      <span>{children}</span>
      {withArrow && !isLoading && (
        <span className="auth-button__arrow" aria-hidden="true">
          &rarr;
        </span>
      )}
    </button>
  );
}
